import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  XMarkIcon, MagnifyingGlassIcon, CalendarIcon, 
  ArrowDownTrayIcon, CheckCircleIcon, CurrencyDollarIcon, DocumentTextIcon,
  PresentationChartLineIcon, MapPinIcon, CameraIcon, IdentificationIcon,
  UserPlusIcon, StarIcon, MapIcon, ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon, BriefcaseIcon, MapPinIcon as MapPinSolid,
  GlobeAmericasIcon, ArrowRightOnRectangleIcon, FingerPrintIcon, BanknotesIcon,
  TableCellsIcon, DocumentArrowDownIcon, ClockIcon, TrophyIcon, GiftIcon,
  ShieldCheckIcon, ChartBarIcon 
} from "@heroicons/react/24/outline";

// Corrected Path: back 3 levels to reach src/
import { 
  getManagerPolicyAckStatus, 
  recommendReward, 
  getManagerRequests, 
  getManagerHistory, 
  approveManagerRequest, 
  rejectManagerRequest,
  offboardingAPI,
  getManagerAttendanceCorrections
} from "../../../services/employee.service";

const employeeDataMaster = {
  "EMP-2025-099": {
    code: "EMP-2025-099",
    firstName: "Johnathan",
    middleName: "Alexander",
    lastName: "Doe",
    type: "Permanent",
    doj: "2022-03-10",
    status: "Active",
    currentSalary: 850000,
    emergencyMobile: "+91 9988776655",
  }
};

const TeamRequests = () => {
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [managerHistory, setManagerHistory] = useState([]);
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  // Policy States
  const [policyLogs, setPolicyLogs] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(false);

  // Exit Management States
  const [exitRequests, setExitRequests] = useState([]);
  const [exitLoading, setExitLoading] = useState(false);

  // Manager Action States
  const [mgmtActionType, setMgmtActionType] = useState("Salary Hike");
  const [mgmtEmpId, setMgmtEmpId] = useState("");
  const [rewardAmount, setRewardAmount] = useState("");
  const [rewardType, setRewardType] = useState("Performance Bonus");

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Reports States
  const [reportType, setReportType] = useState("timesheet");
  const [reportPeriod, setReportPeriod] = useState("monthly");
  const [reportDateRange, setReportDateRange] = useState({ from: '', to: '' });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Team Attendance States
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [attendanceDateRange, setAttendanceDateRange] = useState({ from: '', to: '' });
  const [uniqueEmployees, setUniqueEmployees] = useState([]);
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [showAllRecords, setShowAllRecords] = useState(false);

  const statusLabel = (status) => {
    if (status === "PENDING_MANAGER") return "Pending";
    if (status === "MANAGER_APPROVED") return "Approved";
    if (status === "MANAGER_REJECTED") return "Rejected";
    return status;
  };

  useEffect(() => {
    if (activeTab === "approved" || activeTab === "rejected") {
      fetchManagerHistory();
    } else if (activeTab === "exit") {
      fetchExitRequests();
    } else if (activeTab === "team-attendance") {
      fetchTeamAttendance();
    } else {
      fetchManagerRequests();
    }
  }, [activeTab, dateFilter]);

  useEffect(() => {
    if (activeTab === "team-attendance") {
      applyAttendanceFilters();
    }
  }, [teamAttendance, selectedEmployees, attendanceDateRange, showAllRecords]);

  const fetchTeamAttendance = async () => {
    try {
      const { getTeamAttendanceRecords } = await import('../../../services/employee.service');
      const data = await getTeamAttendanceRecords(200);
      setTeamAttendance(data);
      
      const employees = [...new Set(data.map(r => r.emp_code))].map(code => {
        const record = data.find(r => r.emp_code === code);
        return { code, name: record.emp_name };
      });
      setUniqueEmployees(employees);
    } catch (err) {
      console.error("Failed to fetch team attendance", err);
    }
  };

  const applyAttendanceFilters = () => {
    let filtered = [...teamAttendance];

    // Filter by last 7 days if not showing all
    if (!showAllRecords) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(r => new Date(r.date) >= sevenDaysAgo);
    }

    if (selectedEmployees.length > 0) {
      filtered = filtered.filter(r => selectedEmployees.includes(r.emp_code));
    }

    if (attendanceDateRange.from) {
      filtered = filtered.filter(r => new Date(r.date) >= new Date(attendanceDateRange.from));
    }

    if (attendanceDateRange.to) {
      filtered = filtered.filter(r => new Date(r.date) <= new Date(attendanceDateRange.to));
    }

    setFilteredAttendance(filtered);
  };

  const toggleEmployeeSelection = (empCode) => {
    setSelectedEmployees(prev =>
      prev.includes(empCode) ? prev.filter(e => e !== empCode) : [...prev, empCode]
    );
  };

  const downloadAttendanceExcel = () => {
    const dataToExport = filteredAttendance.length > 0 ? filteredAttendance : teamAttendance;
    
    const metadataRows = [
      { 'Report Information': 'Report Type', 'Value': 'Team Attendance Records' },
      { 'Report Information': 'Generated By', 'Value': localStorage.getItem("emp_code") || "MANAGER" },
      { 'Report Information': 'Generation Time', 'Value': new Date().toLocaleString() },
      { 'Report Information': 'Total Records', 'Value': dataToExport.length },
      { 'Report Information': '', 'Value': '' }
    ];

    const headerRow = {
      'Employee Code': 'Employee Code',
      'Employee Name': 'Employee Name',
      'Date': 'Date',
      'Check-In': 'Check-In',
      'Check-Out': 'Check-Out',
      'Work Hours': 'Work Hours',
      'Status': 'Status'
    };

    const dataRows = dataToExport.map(record => ({
      'Employee Code': record.emp_code,
      'Employee Name': record.emp_name,
      'Date': record.date,
      'Check-In': record.check_in_time,
      'Check-Out': record.check_out_time,
      'Work Hours': record.work_hours,
      'Status': record.status
    }));

    const allRows = [...metadataRows, headerRow, ...dataRows];
    const ws = XLSX.utils.json_to_sheet(allRows);
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Attendance');
    XLSX.writeFile(wb, `team_attendance_${new Date().getTime()}.xlsx`);
  };

  const fetchManagerHistory = async () => {
    try {
      const data = await getManagerHistory(dateFilter);
      console.log("MANAGER HISTORY RAW →", data);

      const mapped = data.map(r => {
        let details = r.employee_reason || "—";
        
        // Extract details from payload if available
        if (r.payload) {
          try {
            const payload = typeof r.payload === 'string' ? JSON.parse(r.payload) : r.payload;
            if (payload.reason) details = payload.reason;
            if (payload.hours) details += ` (${payload.hours} hours)`;
            if (payload.date) details += ` - ${payload.date}`;
          } catch (e) {
            // Keep original details if payload parsing fails
          }
        }
        
        return {
          id: r.id,
          empId: r.emp_code,
          name: r.employee_name || r.emp_code,
          category: mapCategory(r.request_type),
          type: r.request_type,
          details: details,
          date: r.manager_action_at
            ? new Date(r.manager_action_at).toLocaleDateString()
            : "—",
          status: r.status,
          reason: r.manager_reason
        };
      });

      setManagerHistory(mapped);
    } catch (err) {
      console.error("Failed to load manager history", err);
    }
  };

  const fetchManagerRequests = async () => {
    try {
      let data = [];
      
      try {
        data = await getManagerRequests(); // Unified system
        console.log("UNIFIED MANAGER REQUESTS →", data);
      } catch (err) {
        console.log("Unified system not available, using old system");
        data = [];
      }
      
      // Fetch attendance corrections
      try {
        const correctionsData = await getManagerAttendanceCorrections();
        console.log("ATTENDANCE CORRECTIONS →", correctionsData);
        data = [...data, ...correctionsData];
      } catch (err) {
        console.log("Attendance corrections not available");
      }
      
      // Always fetch OT requests from overtime system
      try {
        const otResponse = await fetch("http://localhost:8000/overtime/manager", {
          headers: { "x-emp-code": localStorage.getItem("emp_code") }
        });
        
        if (otResponse.ok) {
          const otData = await otResponse.json();
          console.log("OT REQUESTS FOR MANAGER →", otData);
          
          // Convert old format to new format
          const convertedOT = otData.map(row => ({
            id: row[0],
            emp_code: row[1],
            employee_name: row[1],
            request_type: "OVERTIME",
            status: row[5],
            submitted_at: row[9],
            employee_reason: `${row[3]} hours overtime on ${row[2]}`,
            payload: JSON.stringify({
              date: row[2],
              hours: row[3],
              reason: row[4]
            })
          }));
          
          // Merge with unified requests
          data = [...data, ...convertedOT];
        }
      } catch (err) {
        console.log("OT system not available");
      }

      const mapped = data.map(r => {
        let details = r.employee_reason || "—";
        let amount = 0;
        let hasReceipt = false;
        
        // Extract details from payload if available
        if (r.payload) {
          try {
            const payload = typeof r.payload === 'string' ? JSON.parse(r.payload) : r.payload;
            if (payload.reason) details = payload.reason;
            if (payload.hours) details += ` (${payload.hours} hours)`;
            if (payload.date) details += ` - ${payload.date}`;
            if (payload.amount) {
              amount = payload.amount;
              details = `${payload.category || 'Expense'}: ₹${payload.amount} - ${payload.description}`;
            }
            if (payload.receipt_url) hasReceipt = true;
          } catch (e) {
            // Keep original details if payload parsing fails
          }
        }
        
        return {
          id: r.id,
          empId: r.emp_code,
          name: r.employee_name || r.emp_code,
          category: mapCategory(r.request_type),
          type: r.request_type,
          details: details,
          amount: amount,
          hasReceipt: hasReceipt,
          date: r.submitted_at
            ? new Date(r.submitted_at).toLocaleDateString()
            : "—",
          status: r.status
        };
      });

      setRequests(mapped);
    } catch (err) {
      console.error("Failed to load manager requests", err);
    }
  };

  const mapCategory = (type = "") => {
    if (!type) return "all";

    if (type.includes("LEAVE")) return "leave";
    if (type.includes("TIMESHEET")) return "timesheet";
    if (type.includes("ATTENDANCE")) return "attendance";
    if (type.includes("OVERTIME")) return "attendance";
    if (type.includes("EXPENSE")) return "expense";
    if (type.includes("REWARD")) return "rewards";
    if (type.includes("POLICY")) return "policies";

    return "all";
  };

  // --- API INTEGRATION: FETCH POLICY STATUS ---
  useEffect(() => {
    if (activeTab === "policies") {
      fetchPolicyStatus();
    }
  }, [activeTab]);

  const fetchPolicyStatus = async () => {
    try {
      setPolicyLoading(true);
      const response = await getManagerPolicyAckStatus();
      setPolicyLogs(Array.isArray(response.data) ? response.data : []); 
    } catch (err) {
      console.error("Failed to fetch policy status", err);
    } finally {
      setPolicyLoading(false);
    }
  };

  // --- API INTEGRATION: FETCH EXIT REQUESTS ---
  const fetchExitRequests = async () => {
    try {
      setExitLoading(true);
      const response = await offboardingAPI.getPendingApprovals();
      console.log("📋 Exit requests response:", response);
      
      // Handle both response.data.pending_resignations and response.pending_resignations
      const exitData = response.data?.pending_resignations || response.pending_resignations || [];
      
      const formattedExitRequests = exitData.map(req => ({
        id: req.id,
        empId: req.emp_code,
        name: req.employee_name || req.emp_code,
        category: "exit",
        type: req.status === 'pending_final_manager_approval' ? "FINAL_RESIGNATION" : "RESIGNATION",
        details: req.status === 'pending_final_manager_approval' 
          ? `Final submission completed - Handover: ${req.handover_completed ? 'Yes' : 'No'}, Assets: ${req.assets_confirmed ? 'Confirmed' : 'Pending'}`
          : `${req.reason} - Notice: ${req.notice_period_days} days`,
        date: new Date(req.submitted_at).toLocaleDateString(),
        status: req.status === 'pending_final_manager_approval' ? 'PENDING_FINAL_MANAGER' : 'PENDING_MANAGER',
        resignationDate: req.resignation_date,
        requestedLwd: req.requested_lwd,
        noticePeriod: req.notice_period_days,
        reason: req.reason,
        isFinalSubmission: req.final_submission || false,
        handoverCompleted: req.handover_completed || false,
        assetsConfirmed: req.assets_confirmed || false
      }));
      
      setExitRequests(formattedExitRequests);
    } catch (err) {
      console.error("Failed to fetch exit requests", err);
      setExitRequests([]);
    } finally {
      setExitLoading(false);
    }
  };

  // --- HANDLE EXIT REQUEST APPROVAL/REJECTION ---
  const handleExitAction = async (resignationId, actionType, reason = null) => {
    try {
      const decision = {
        approve: actionType === "Approve",
        reason: reason
      };
      
      await offboardingAPI.approveResignation(resignationId, decision);
      
      // Refresh exit requests
      await fetchExitRequests();
      
      // Show success message
      alert(`Resignation ${actionType.toLowerCase()}d successfully!`);
      
    } catch (err) {
      console.error(`Failed to ${actionType.toLowerCase()} resignation:`, err);
      alert(`Failed to ${actionType.toLowerCase()} resignation. Please try again.`);
    }
  };

  const handleAction = async (id, actionType) => {
    if (actionType === "Reject") {
      const req = requests.find(r => r.id === id);
      if (req) {
        setRequestToReject(req);
        setShowRejectModal(true);
        setRejectionReason("");
      }
      return;
    }

    try {
      const req = requests.find(r => r.id === id);
      
      // Handle attendance corrections separately
      if (req && req.type === 'ATTENDANCE_CORRECTION') {
        await fetch(`http://localhost:8000/manager/attendance-corrections/${id}/approve`, {
          method: 'POST',
          headers: {
            "x-emp-code": localStorage.getItem("emp_code")
          }
        });
        await fetchManagerRequests();
        return;
      }
      
      // Try unified system first, fallback to old system
      try {
        await approveManagerRequest(id);
      } catch (err) {
        console.log("Unified system not available, using old OT system");
        
        // Use old OT approval system
        const response = await fetch(`http://localhost:8000/overtime/${id}/approve`, {
          method: 'POST',
          headers: {
            "x-emp-code": localStorage.getItem("emp_code")
          }
        });
        
        if (!response.ok) {
          throw new Error('Old system approval failed');
        }
      }

      setRequests(prev =>
        prev.map(r =>
          r.id === id
            ? { ...r, status: "APPROVED" }
            : r
        )
      );

      await fetchManagerRequests();
    } catch (err) {
      console.error("Manager action failed", err);
      alert("Approval failed. Please try again.");
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) return;

    try {
      if (requestToReject.category === 'exit') {
        // Handle exit request rejection
        await handleExitAction(requestToReject.id, 'Reject', rejectionReason);
      } else if (requestToReject.type === 'ATTENDANCE_CORRECTION') {
        // Handle attendance correction rejection
        await fetch(`http://localhost:8000/manager/attendance-corrections/${requestToReject.id}/reject`, {
          method: 'POST',
          headers: {
            "x-emp-code": localStorage.getItem("emp_code"),
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ reason: rejectionReason })
        });
        await fetchManagerRequests();
      } else {
        // Handle other request rejections
        try {
          await rejectManagerRequest(requestToReject.id, rejectionReason);
        } catch (err) {
          console.log("Unified system not available, using old OT system");
          
          // Use old OT rejection system
          const response = await fetch(`http://localhost:8000/overtime/${requestToReject.id}/reject`, {
            method: 'POST',
            headers: {
              "x-emp-code": localStorage.getItem("emp_code"),
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ reason: rejectionReason })
          });
          
          if (!response.ok) {
            throw new Error('Old system rejection failed');
          }
        }

        setRequests(prev =>
          prev.map(r =>
            r.id === requestToReject.id
              ? { ...r, status: "REJECTED" }
              : r
          )
        );

        await fetchManagerRequests();
      }

      setShowRejectModal(false);
      setRejectionReason("");
    } catch (err) {
      console.error("Reject failed", err);
      alert("Rejection failed. Please try again.");
    }
  };

  const downloadTimesheet = async (requestId, format) => {
    try {
      const response = await fetch(`http://localhost:8000/unified_approval/timesheet/download/${requestId}?format=${format}`, {
        headers: {
          "x-emp-code": localStorage.getItem("emp_code")
        }
      });
      
      if (response.status === 404) {
        alert('Timesheet download not available. This timesheet may not have detailed data.');
        return;
      }
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `timesheet_${requestId}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Download not available');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Download not available for this timesheet');
    }
  };

 const submitManagerRequest = async (e) => {
  e.preventDefault();
  if (!mgmtEmpId) return alert("Please enter Employee ID");

  // This is the clean payload matching your new Python RewardRecommendSchema
  const payload = {
    emp_code: mgmtEmpId.trim(),
    employee_id: mgmtEmpId.trim(),
    reward_type: "BONUS", // Keep as BONUS to ensure it maps to existing bonus rules
    reward_rule_id: 2,
    recommended_amount: Number(rewardAmount) || 0,
    recommendation_note: `${mgmtActionType}: ${rewardType}`, // e.g., "SALARY HIKE: Performance"
    status: "PENDING"
  };

  console.log("🚀 Sending Final Payload to Backend:", payload);

  try {
    const response = await recommendReward(payload);
    
    alert(`✅ Success! ${mgmtActionType} request for ${mgmtEmpId} sent.`);
    
    // UI Update
    const newEntry = {
      id: `REW-${Date.now()}`,
      empId: mgmtEmpId,
      name: "Team Member",
      type: mgmtActionType, 
      details: payload.recommendation_note,
      date: new Date().toLocaleDateString(),
      status: "Forwarded to HR Dept",
      category: "rewards"
    };
    setRequests(prev => [newEntry, ...prev]);
    
    // Clear Form
    setMgmtEmpId("");
    setRewardAmount("");

  } catch (error) {
    console.error("Submission Error:", error.response?.data);
    alert("Submission failed. Check if server is running.");
  }
};

  const filteredRequests = (() => {
    let dataSource = requests;
    
    // Use history data for approved/rejected tabs
    if (activeTab === "approved" || activeTab === "rejected") {
      dataSource = managerHistory.filter(req => {
        if (activeTab === "approved") {
          return req.status === "PENDING_HR" || req.status === "HR_APPROVED" || req.status === "APPLIED_TO_RECORDS";
        }
        if (activeTab === "rejected") {
          return req.status === "MANAGER_REJECTED";
        }
        return false;
      });
    } else if (activeTab === "exit") {
      // Use exit requests data
      dataSource = exitRequests;
    } else {
      // Filter current requests
      dataSource = requests.filter(req => {
        const matchesTab = activeTab === "all" || req.category === activeTab;
        return matchesTab;
      });
    }
    
    // Apply search filter
    return dataSource.filter(req => {
      const matchesSearch = (req.empId || "").toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  })();

  const getEmpDetails = (id) => employeeDataMaster[id] || employeeDataMaster["EMP-2025-099"];

  const generateReport = async (format) => {
    setIsGeneratingReport(true);
    try {
      let fromDate, toDate;
      
      if (reportPeriod === 'custom' && reportDateRange.from && reportDateRange.to) {
        fromDate = reportDateRange.from;
        toDate = reportDateRange.to;
      } else {
        const now = new Date();
        
        switch (reportPeriod) {
          case 'daily':
            fromDate = toDate = now.toISOString().split('T')[0];
            break;
          case 'monthly':
            fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
            break;
          case 'quarterly':
            const quarter = Math.floor(now.getMonth() / 3);
            fromDate = new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), quarter * 3 + 3, 0).toISOString().split('T')[0];
            break;
          case 'halfyearly':
            const half = now.getMonth() < 6 ? 0 : 6;
            fromDate = new Date(now.getFullYear(), half, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), half + 6, 0).toISOString().split('T')[0];
            break;
          case 'yearly':
            fromDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
            break;
          default:
            fromDate = toDate = now.toISOString().split('T')[0];
        }
      }
      
      // Get data based on report type
      let reportData = [];
      
      if (reportType === 'team-attendance') {
        // Fetch team attendance records
        const { getTeamAttendanceRecords } = await import('../../../services/employee.service');
        const attendanceData = await getTeamAttendanceRecords(200);
        reportData = attendanceData.filter(record => {
          const recDate = new Date(record.date);
          return recDate >= new Date(fromDate) && recDate <= new Date(toDate);
        });
      } else if (reportType === 'team-leaves') {
        // Fetch team leave requests
        const { getTeamLeaveRequests } = await import('../../../services/employee.service');
        const leavesData = await getTeamLeaveRequests();
        reportData = leavesData.filter(leave => {
          const leaveDate = new Date(leave.applied_at);
          return leaveDate >= new Date(fromDate) && leaveDate <= new Date(toDate);
        });
      } else if (reportType === 'approved' || reportType === 'rejected') {
        reportData = managerHistory.filter(req => {
          const matchesType = reportType === 'approved' ? 
            (req.status === "PENDING_HR" || req.status === "HR_APPROVED" || req.status === "APPLIED_TO_RECORDS") :
            req.status === "MANAGER_REJECTED";
          const reqDate = new Date(req.date);
          const matchesDate = reqDate >= new Date(fromDate) && reqDate <= new Date(toDate);
          return matchesType && matchesDate;
        });
      } else {
        reportData = filteredRequests.filter(req => {
          const matchesType = reportType === 'all' || req.category === reportType;
          const reqDate = new Date(req.date);
          const matchesDate = reqDate >= new Date(fromDate) && reqDate <= new Date(toDate);
          return matchesType && matchesDate;
        });
      }
      
      // Get current user info
      const currentUser = localStorage.getItem("emp_code") || "MANAGER";
      const generationTime = new Date().toLocaleString();
      const reportTitle = reportType.charAt(0).toUpperCase() + reportType.slice(1) + " Report";
      const periodText = reportPeriod === 'custom' ? `${fromDate} to ${toDate}` : reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1);
      
      if (format === 'excel') {
        // Create metadata rows
        const metadataRows = [
          { 'Report Information': 'Report Type', 'Value': reportTitle },
          { 'Report Information': 'Report Period', 'Value': periodText },
          { 'Report Information': 'Generated By', 'Value': currentUser },
          { 'Report Information': 'Generation Time', 'Value': generationTime },
          { 'Report Information': '', 'Value': '' } // Empty row
        ];
        
        // Tab-specific headers matching the UI table headers
        let headerRow, dataRows;
        
        if (reportType === 'team-attendance') {
          headerRow = { 'Employee Code': 'Employee Code', 'Employee Name': 'Employee Name', 'Date': 'Date', 'Check-In': 'Check-In', 'Check-Out': 'Check-Out', 'Work Hours': 'Work Hours', 'Status': 'Status' };
          dataRows = reportData.map(record => ({
            'Employee Code': record.emp_code,
            'Employee Name': record.emp_name,
            'Date': record.date,
            'Check-In': record.check_in_time,
            'Check-Out': record.check_out_time,
            'Work Hours': record.work_hours,
            'Status': record.status
          }));
        } else if (reportType === 'team-leaves') {
          headerRow = { 'Employee Code': 'Employee Code', 'Employee Name': 'Employee Name', 'Leave Type': 'Leave Type', 'Start Date': 'Start Date', 'End Date': 'End Date', 'Days': 'Days', 'Reason': 'Reason', 'Status': 'Status' };
          dataRows = reportData.map(leave => ({
            'Employee Code': leave.emp_code,
            'Employee Name': leave.emp_name,
            'Leave Type': leave.leave_type,
            'Start Date': leave.start_date,
            'End Date': leave.end_date,
            'Days': leave.days,
            'Reason': leave.reason,
            'Status': leave.status
          }));
        } else if (reportType === 'policies') {
          headerRow = { 'Employee Name': 'Employee Name', 'Policy Document': 'Policy Document', 'Signed Date': 'Signed Date', 'Status': 'Status' };
          dataRows = reportData.map(req => ({
            'Employee Name': req.name,
            'Policy Document': req.type || 'Policy Document',
            'Signed Date': req.date,
            'Status': req.status
          }));
        } else {
          // Standard table headers for all other tabs
          headerRow = { 'Employee Details': 'Employee Details', 'Category / Type': 'Category / Type', 'Submission Summary': 'Submission Summary', 'Current Status': 'Current Status', 'Action': 'Action' };
          dataRows = reportData.map(req => ({
            'Employee Details': `${req.name} (${req.empId})`,
            'Category / Type': `${req.category} - ${req.type}`,
            'Submission Summary': `${req.details}${req.amount ? ` - ₹${req.amount}` : ''} - ${req.date}`,
            'Current Status': req.status,
            'Action': req.reason || 'PROCESSED'
          }));
        }
        
        const allRows = [...metadataRows, headerRow, ...dataRows];
        const ws = XLSX.utils.json_to_sheet(allRows);
        
        // Style the header rows
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = 0; R <= 5; R++) {
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cell_address = XLSX.utils.encode_cell({ c: C, r: R });
            if (!ws[cell_address]) continue;
            ws[cell_address].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "E3F2FD" } }
            };
          }
        }
        
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Team Report');
        XLSX.writeFile(wb, `${reportType}_${reportPeriod}_report_${new Date().getTime()}.xlsx`);
      } else {
        const doc = new jsPDF();
        
        // Header with metadata
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(reportTitle, 20, 20);
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text(`Report Period: ${periodText}`, 20, 35);
        doc.text(`Generated By: ${currentUser}`, 20, 45);
        doc.text(`Generation Time: ${generationTime}`, 20, 55);
        
        // Add a line separator
        doc.setLineWidth(0.5);
        doc.line(20, 65, 190, 65);
        
        let tableHeaders, tableData;
        
        if (reportType === 'policies') {
          tableHeaders = ['Employee Name', 'Policy Document', 'Signed Date', 'Status'];
          tableData = reportData.map(req => [
            req.name,
            req.type || 'Policy Document',
            req.date,
            req.status
          ]);
        } else {
          tableHeaders = ['Employee Details', 'Category / Type', 'Submission Summary', 'Current Status', 'Action'];
          tableData = reportData.map(req => [
            `${req.name} (${req.empId})`,
            `${req.category} - ${req.type}`,
            `${req.details.substring(0, 20)}${req.details.length > 20 ? '...' : ''}${req.amount ? ` - ₹${req.amount}` : ''}`,
            req.status,
            req.reason || 'PROCESSED'
          ]);
        }
        
        doc.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: 75,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { top: 75 }
        });
        
        // Add footer with summary
        const finalY = doc.lastAutoTable.finalY || 75;
        doc.setFontSize(10);
        doc.text(`Total Records: ${reportData.length}`, 20, finalY + 15);
        doc.text(`Report Generated: ${generationTime}`, 20, finalY + 25);
        
        doc.save(`${reportType}_${reportPeriod}_report_${new Date().getTime()}.pdf`);
      }
    } catch (err) {
      console.error('Report generation error:', err);
      alert('Report generation failed');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6 font-roboto text-slate-900">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Roboto:wght@400;500;700;900&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        th, td { border: 1px solid #e2e8f0 !important; }
      `}} />

      {/* MODALS */}
      <AnimatePresence>
        {employeeProfile && (
           <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
             <motion.div initial={{opacity: 0, scale: 0.95}} animate={{opacity: 1, scale: 1}} exit={{opacity: 0}} className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl rounded-lg border border-slate-300">
               <div className="sticky top-0 bg-white border-b-2 p-4 flex justify-between items-center z-10">
                 <div>
                   <h2 className="text-xl font-700 font-poppins text-slate-900 uppercase">EMPLOYEE MASTER FILE</h2>
                   <p className="text-[11px] text-blue-700 font-bold tracking-widest">{employeeProfile.empId}</p>
                 </div>
                 <button onClick={()=>setEmployeeProfile(null)} className="p-1 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 stroke-[2.5]"/></button>
               </div>
               <div className="p-6">
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                   {[
                     { label: "Employee Code", val: getEmpDetails(employeeProfile.empId).code },
                     { label: "Full Name", val: `${getEmpDetails(employeeProfile.empId).firstName} ${getEmpDetails(employeeProfile.empId).lastName}` },
                     { label: "Status", val: getEmpDetails(employeeProfile.empId).status },
                     { label: "Contact", val: getEmpDetails(employeeProfile.empId).emergencyMobile },
                   ].map((item, idx) => (
                     <div key={idx} className="border-l-4 border-slate-200 pl-3 bg-slate-50 py-1">
                       <p className="text-[10px] font-bold text-slate-500 uppercase">{item.label}</p>
                       <p className="text-xs font-700 text-slate-900">{item.val}</p>
                     </div>
                   ))}
                 </div>
               </div>
             </motion.div>
           </div>
        )}

        {showRejectModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-lg rounded-xl p-6">
              <h3 className="text-lg font-bold text-red-800 mb-4">Reject Request</h3>
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Reason for rejection..." className="w-full border p-3 rounded-lg text-sm mb-4" rows={4} />
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 bg-slate-100 rounded text-sm">Cancel</button>
                <button onClick={handleRejectSubmit} className="px-4 py-2 bg-red-600 text-white rounded text-sm">Confirm</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="max-w-[1600px] mx-auto space-y-4">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-center pb-4 border-b border-slate-300">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-700 rounded shadow-lg shadow-blue-200">
              <PresentationChartLineIcon className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-700 font-poppins text-slate-900 tracking-tight">Team Requests & Approvals</h1>
          </div>
          <div className="flex flex-col md:flex-row gap-3 items-center">
            {(activeTab === "approved" || activeTab === "rejected") && (
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                  className="border rounded px-2 py-1 text-xs"
                  placeholder="From"
                />
                <span className="text-xs text-slate-500">to</span>
                <input
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                  className="border rounded px-2 py-1 text-xs"
                  placeholder="To"
                />
              </div>
            )}
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="text" placeholder="Search by Employee ID..." className="pl-10 pr-4 py-2 bg-white border rounded text-xs w-72" onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>

        {/* ACTIONS & REWARDS SECTION */}
        <div className="bg-white p-5 border border-slate-200 rounded-lg shadow-sm">
          <h2 className="text-[14px] font-700 font-poppins text-slate-800 uppercase tracking-wide mb-5 flex items-center gap-2 border-b border-slate-100 pb-3">
            <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
            Personnel Actions & Rewards Management
          </h2>
          <form onSubmit={submitManagerRequest} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500">Employee ID</label>
              <input type="text" value={mgmtEmpId} onChange={(e)=>setMgmtEmpId(e.target.value)} className="w-full border rounded p-2 text-xs" placeholder="Ex: EMP-101" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500">Action Type</label>
              <select value={mgmtActionType} onChange={(e) => setMgmtActionType(e.target.value)} className="w-full border rounded p-2 text-xs bg-white">
                <option>Salary Hike</option>
                <option>Promotion</option>
                <option>Bonus</option>
                <option>Incentives</option>
              </select>
            </div>
            {(mgmtActionType === "Bonus" || mgmtActionType === "Incentives") && (
              <>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-slate-500">Amount (₹)</label>
                  <input type="number" value={rewardAmount} onChange={(e)=>setRewardAmount(e.target.value)} className="w-full border rounded p-2 text-xs" placeholder="Amount" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-[10px] font-bold uppercase text-slate-500">Reward Category</label>
                  <select value={rewardType} onChange={(e)=>setRewardType(e.target.value)} className="w-full border rounded p-2 text-xs bg-white">
                    <option>Performance Bonus</option>
                    <option>Spot Award</option>
                  </select>
                </div>
              </>
            )}
            <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded text-[11px] font-700 uppercase tracking-wider h-[38px]">Submit to HR</button>
          </form>
        </div>

        {/* TABS */}
        <div className="mt-6">
          <div className="flex border-b border-slate-300 overflow-x-auto no-scrollbar gap-1 bg-white px-2 rounded-t-lg">
            {[
              { id: "all", label: "Master Inbox", icon: ClipboardDocumentCheckIcon },
              { id: "timesheet", label: "Timesheets", icon: DocumentTextIcon },
              { id: "attendance", label: "Attendance & OT", icon: FingerPrintIcon },
              { id: "expense", label: "Expenses", icon: BanknotesIcon },
              { id: "rewards", label: "Bonus & Rewards", icon: TrophyIcon },
              { id: "policies", label: "Policy Acks", icon: ShieldCheckIcon },
              { id: "approved", label: "My Approvals", icon: CheckCircleIcon },
              { id: "rejected", label: "My Rejections", icon: XMarkIcon },
              { id: "exit", label: "Exit Mngmt", icon: ArrowRightOnRectangleIcon },
              { id: "team-attendance", label: "Team Attendance", icon: ClockIcon },
              { id: "fieldwork", label: "Field Reports", icon: BriefcaseIcon },
              { id: "correction", label: "Corrections", icon: ExclamationTriangleIcon },
              { id: "leave", label: "Leave Req", icon: CalendarIcon },
              { id: "onboarding", label: "Onboarding", icon: UserPlusIcon },
              { id: "reports", label: "Reports", icon: ChartBarIcon },
            ].map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-4 flex items-center gap-2 text-[12px] font-700 font-poppins uppercase tracking-tight transition-all border-b-[3px] whitespace-nowrap ${
                  activeTab === tab.id ? "border-blue-700 text-blue-700 bg-blue-50/50" : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200"
                }`}
              >
                <tab.icon className={`h-4 w-4 stroke-[2.5] ${activeTab === tab.id ? "text-blue-700" : "text-slate-400"}`} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-b-lg border-x border-b border-slate-200 shadow-sm overflow-x-auto">
            {activeTab === "team-attendance" ? (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-4">
                    <h3 className="text-lg font-700 font-poppins text-slate-800">Team Attendance Records</h3>
                    <button
                      onClick={() => setShowAllRecords(!showAllRecords)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold"
                    >
                      {showAllRecords ? 'Show Last 7 Days' : 'View All'}
                    </button>
                  </div>
                  <button onClick={downloadAttendanceExcel} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold">
                    <ArrowDownTrayIcon className="h-5 w-5" />Download Excel
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-1 bg-slate-50 rounded-lg border p-4">
                    <h4 className="text-sm font-bold text-slate-700 mb-4">Filters</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-bold text-slate-600 mb-2 block">Date Range</label>
                        <input type="date" value={attendanceDateRange.from} onChange={(e) => setAttendanceDateRange(prev => ({ ...prev, from: e.target.value }))} className="w-full border rounded p-2 text-sm mb-2" />
                        <input type="date" value={attendanceDateRange.to} onChange={(e) => setAttendanceDateRange(prev => ({ ...prev, to: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-slate-600 mb-2 block">Select Employees</label>
                        <div className="relative mb-2">
                          <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="text" placeholder="Search..." value={attendanceSearchTerm} onChange={(e) => setAttendanceSearchTerm(e.target.value)} className="w-full pl-8 pr-3 py-2 border rounded text-sm" />
                        </div>
                        <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1 bg-white">
                          {uniqueEmployees.filter(emp => emp.code.toLowerCase().includes(attendanceSearchTerm.toLowerCase()) || emp.name.toLowerCase().includes(attendanceSearchTerm.toLowerCase())).map(emp => (
                            <label key={emp.code} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                              <input type="checkbox" checked={selectedEmployees.includes(emp.code)} onChange={() => toggleEmployeeSelection(emp.code)} className="rounded" />
                              <span className="text-sm">{emp.name} ({emp.code})</span>
                            </label>
                          ))}
                        </div>
                        {selectedEmployees.length > 0 && (
                          <button onClick={() => setSelectedEmployees([])} className="mt-2 text-xs text-red-600 hover:underline">Clear Selection ({selectedEmployees.length})</button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="p-4 border-b bg-slate-50">
                        <h4 className="text-sm font-bold text-slate-700">Records ({(filteredAttendance.length > 0 ? filteredAttendance : teamAttendance).length})</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-slate-50 text-slate-700 text-xs font-bold uppercase">
                            <tr>
                              <th className="py-3 px-4 text-left">Employee Code</th>
                              <th className="py-3 px-4 text-left">Employee Name</th>
                              <th className="py-3 px-4 text-left">Date</th>
                              <th className="py-3 px-4 text-left">Check-In</th>
                              <th className="py-3 px-4 text-left">Check-Out</th>
                              <th className="py-3 px-4 text-left">Work Hours</th>
                              <th className="py-3 px-4 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(filteredAttendance.length > 0 ? filteredAttendance : teamAttendance).map((record, idx) => (
                              <tr key={idx} className="hover:bg-blue-50/30">
                                <td className="py-3 px-4 text-sm font-bold text-slate-900">{record.emp_code}</td>
                                <td className="py-3 px-4 text-sm text-slate-700">{record.emp_name}</td>
                                <td className="py-3 px-4 text-sm text-slate-600">{record.date}</td>
                                <td className="py-3 px-4 text-sm text-slate-600">{record.check_in_time}</td>
                                <td className="py-3 px-4 text-sm text-slate-600">{record.check_out_time}</td>
                                <td className="py-3 px-4 text-sm text-slate-600">{record.work_hours}</td>
                                <td className="py-3 px-4 text-center">
                                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                                    record.status === 'PRESENT' ? 'bg-green-100 text-green-700' :
                                    record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>{record.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === "reports" ? (
              <div className="p-6">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-lg font-700 font-poppins text-slate-800 mb-6 flex items-center gap-2">
                    <ChartBarIcon className="h-6 w-6 text-blue-600" />
                    Generate Team Reports
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Report Type Selection */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Report Type</label>
                      <select 
                        value={reportType} 
                        onChange={(e) => setReportType(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg p-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="team-attendance">Team Attendance Records</option>
                        <option value="team-leaves">Team Leave Requests</option>
                        <option value="timesheet">Timesheets</option>
                        <option value="attendance">Attendance & OT</option>
                        <option value="expense">Expenses</option>
                        <option value="rewards">Bonus & Rewards</option>
                        <option value="policies">Policy Acks</option>
                        <option value="approved">My Approvals</option>
                        <option value="rejected">My Rejections</option>
                        <option value="exit">Exit Management</option>
                        <option value="fieldwork">Field Reports</option>
                        <option value="correction">Corrections</option>
                        <option value="leave">Leave Requests</option>
                        <option value="onboarding">Onboarding</option>
                      </select>
                    </div>
                    
                    {/* Report Period Selection */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Report Period</label>
                      <select 
                        value={reportPeriod} 
                        onChange={(e) => setReportPeriod(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg p-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="daily">Daily</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="halfyearly">Half Yearly</option>
                        <option value="yearly">Yearly</option>
                        <option value="custom">Custom Date Range</option>
                      </select>
                    </div>
                    
                    {/* Download Format */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Download Format</label>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => generateReport('excel')}
                          disabled={isGeneratingReport}
                          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white px-4 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                        >
                          <TableCellsIcon className="h-5 w-5" />
                          {isGeneratingReport ? 'Generating...' : 'Excel'}
                        </button>
                        <button 
                          onClick={() => generateReport('pdf')}
                          disabled={isGeneratingReport}
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white px-4 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                        >
                          <DocumentTextIcon className="h-5 w-5" />
                          {isGeneratingReport ? 'Generating...' : 'PDF'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom Date Range */}
                  {reportPeriod === 'custom' && (
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6">
                      <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Custom Date Range</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-600 uppercase">From Date</label>
                          <input 
                            type="date" 
                            value={reportDateRange.from}
                            onChange={(e) => setReportDateRange(prev => ({ ...prev, from: e.target.value }))}
                            className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-600 uppercase">To Date</label>
                          <input 
                            type="date" 
                            value={reportDateRange.to}
                            onChange={(e) => setReportDateRange(prev => ({ ...prev, to: e.target.value }))}
                            className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Report Preview Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5" />
                      Report Information
                    </h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p><span className="font-bold">Report Type:</span> {reportType.charAt(0).toUpperCase() + reportType.slice(1)}</p>
                      <p><span className="font-bold">Period:</span> {reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}</p>
                      {reportPeriod === 'custom' && reportDateRange.from && reportDateRange.to && (
                        <p><span className="font-bold">Date Range:</span> {reportDateRange.from} to {reportDateRange.to}</p>
                      )}
                      <p className="text-xs text-blue-600 mt-2 italic">Reports will include all team members under your management and their respective data for the selected period.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === "policies" ? (
              <table className="min-w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-700 text-[11px] font-700 font-poppins uppercase tracking-wider">
                    <th className="py-4 px-4">Employee Name</th>
                    <th className="py-4 px-4">Policy Document</th>
                    <th className="py-4 px-4">Signed Date</th>
                    <th className="py-4 px-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-roboto">
                  {policyLoading ? (
                    <tr><td colSpan="4" className="py-10 text-center text-slate-400">Loading compliance data...</td></tr>
                  ) : policyLogs.length === 0 ? (
                    <tr><td colSpan="4" className="py-10 text-center text-slate-400">No policy records found.</td></tr>
                  ) : policyLogs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                      <td className="py-4 px-4 font-700 text-sm text-slate-900">{log.employee_name}</td>
                      <td className="py-4 px-4 text-xs text-slate-600">{log.policy_title}</td>
                      <td className="py-4 px-4 text-xs text-slate-500">
                        {log.acknowledged_at ? new Date(log.acknowledged_at).toLocaleString() : "---"}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase border ${
                          log.status === 'Acknowledged' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-orange-50 text-orange-700 border-orange-200'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-700 text-[11px] font-700 font-poppins uppercase tracking-wider">
                    <th className="py-4 px-4 w-1/4">Employee Details</th>
                    <th className="py-4 px-4 w-1/6">Category / Type</th>
                    <th className="py-4 px-4 w-1/4">Submission Summary</th>
                    <th className="py-4 px-4 w-1/6 text-center">Current Status</th>
                    <th className="py-4 px-4 w-1/6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-roboto">
                  {filteredRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-blue-50/30 transition-colors">
                      <td className="py-4 px-4">
                        <div className="font-700 text-slate-900 text-sm">{req.name}</div>
                        <button onClick={()=>setEmployeeProfile(req)} className="text-[11px] text-blue-600 font-bold hover:underline">ID: {req.empId}</button>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${req.category === 'rewards' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-100 text-slate-600'}`}>{req.category}</span>
                        <p className="text-[11px] font-medium text-slate-500 mt-1.5">{req.type}</p>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-xs font-500 text-slate-800 truncate max-w-[200px]">{req.details}</p>
                        {req.reason && (
                          <p className="text-[10px] text-red-600 font-bold mt-1 italic truncate">Reason: {req.reason}</p>
                        )}
                        <p className="text-[10px] text-slate-500 font-bold mt-1 flex items-center gap-1 uppercase"><CalendarIcon className="h-3 w-3" /> {req.date}</p>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase border ${
                          (req.status || "").includes('Forwarded') || (req.status || "").includes('Approved') ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}> {statusLabel(req.status)} </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        {req.category === 'exit' && (req.status === 'PENDING_MANAGER' || req.status === 'PENDING_FINAL_MANAGER') ? (
                          <div className="flex justify-end gap-2">
                            <button 
                              onClick={() => {
                                setRequestToReject(req);
                                setShowRejectModal(true);
                                setRejectionReason("");
                              }} 
                              className="p-1.5 text-slate-400 hover:text-red-600 rounded-md"
                            >
                              <XMarkIcon className="h-5 w-5 stroke-[2.5]"/>
                            </button>
                            <button 
                              onClick={() => handleExitAction(req.id, 'Approve')} 
                              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-1.5 rounded text-[10px] font-bold uppercase"
                            >
                              {req.status === 'PENDING_FINAL_MANAGER' ? 'Final Approve' : 'Approve Exit'}
                            </button>
                          </div>
                        ) : req.status === 'PENDING_MANAGER' || req.status === 'PENDING_FINAL_MANAGER' ? (
                          <div className="flex justify-end gap-2">
                            {req.category === 'expense' && req.hasReceipt && (
                              <button 
                                onClick={() => window.open(`http://localhost:8000/unified_approval/expense/download/${req.id}`, '_blank')}
                                className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200" 
                                title="Download Receipt"
                              >
                                <DocumentArrowDownIcon className="h-5 w-5 stroke-[2.5]"/>
                              </button>
                            )}
                            {req.category === 'timesheet' && (
                              <>
                                <button 
                                  onClick={() => downloadTimesheet(req.id, 'excel')}
                                  className="p-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-md border border-green-200" 
                                  title="Download Excel"
                                >
                                  <TableCellsIcon className="h-5 w-5 stroke-[2.5]"/>
                                </button>
                                <button 
                                  onClick={() => downloadTimesheet(req.id, 'pdf')}
                                  className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-md border border-red-200" 
                                  title="Download PDF"
                                >
                                  <DocumentTextIcon className="h-5 w-5 stroke-[2.5]"/>
                                </button>
                              </>
                            )}
                            <button onClick={() => handleAction(req.id, 'Reject')} className="p-1.5 text-slate-400 hover:text-red-600 rounded-md"><XMarkIcon className="h-5 w-5 stroke-[2.5]"/></button>
                            <button onClick={() => handleAction(req.id, 'Approve')} className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-1.5 rounded text-[10px] font-bold uppercase">{req.category === 'leave' ? 'Approve' : 'Forward'}</button>
                          </div>
                        ) : <span className="text-[10px] font-bold text-slate-400 italic">PROCESSED</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamRequests;